// src/services/ingredients.ts
//
// Ingredient (meal food item) service. Backs the ingredient dashboard list.

import { apiFetch } from "../Component/api/apiFetch";
import { API_URLS } from "../Component/api/url";
import { handleJson } from "./http";

export const getIngredientDashboardList = (
  filter: "" | "active" | "archive" = "",
  createdBy: string | number = "",
  search: string = "",
  page: number = 1,
  size: number = 10,
): Promise<any> =>
  handleJson(
    apiFetch(
      API_URLS.GET_LIST_IN_INGREDIENT_DASHBOARD(filter, createdBy, search, page, size),
      { method: "GET" },
    ),
  );

// Fetch full detail record for a single ingredient. Backs the View / Edit
// pages — the list endpoint only returns the tabular columns, so the
// detail-only fields (measurements, activity, tags, cpf, base_quantity)
// live behind this call.
export const getIngredientDetails = (food_id: string | number): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.GET_INGREDIENT_DETAILS(food_id), { method: "GET" }),
  );

// Search ingredients through the admin listing endpoint. Backs the dish
// calculator's row picker — the ingredient dashboard endpoint is scoped to
// the nutritionist's view, whereas this returns the full admin-visible
// catalogue for composition.
export const searchAllIngredientsAdmin = (
  search: string,
  limit: number = 20,
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.GET_ALL_INGREDIENTS_ADMIN(search, limit), {
      method: "GET",
    }),
  );

// Archive (is_hide: true) or restore (is_hide: false) an ingredient. The
// backend uses `hide_reason` for archive and `restore_reason` for restore —
// only the field relevant to the action is sent. The nutritionist is
// identified server-side from the Bearer token attached by apiFetch.
export const archiveOrRestoreIngredient = (params: {
  food_id: number;
  is_hide: boolean;
  reason: string;
}): Promise<any> => {
  const { food_id, is_hide, reason } = params;
  const body: Record<string, unknown> = { food_id, is_hide };
  if (is_hide) {
    body.hide_reason = reason;
  } else {
    body.restore_reason = reason;
  }
  return handleJson(
    apiFetch(API_URLS.ARCHIVE_OR_RESTORE_INGREDIENT, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  );
};

// Response from CHECK_INGREDIENT_NAME_EXISTS. When `exists` is true the
// caller renders an inline "already exists" error and a View link that
// navigates to `matched_food_id`. `matched_field` distinguishes a
// name-vs-tag match, and `matched_tags` is populated for tag matches.
export interface CheckIngredientNameExistsResponse {
  success?: number | string | boolean;
  exists?: boolean;
  matched_field?: string;
  matched_food_id?: number;
  matched_item_name?: string;
  matched_tags?: string[];
  message?: string;
}

// Check whether an ingredient name (or one of its tags) already matches an
// existing food item. Backs the inline duplicate-name check on the Add /
// Edit ingredient form — fired on debounce after the user types a name.
export const checkIngredientNameExists = (
  ingredient_name: string,
): Promise<CheckIngredientNameExistsResponse> =>
  handleJson(
    apiFetch(API_URLS.CHECK_INGREDIENT_NAME_EXISTS(ingredient_name), {
      method: "GET",
    }),
  );

// Shape of a single category returned by GET_INGREDIENT_CATEGORIES.
export interface IngredientCategory {
  category_id: number;
  category_name: string;
}

// Shape of a single unit returned by GET_UNITS_LIST.
export interface IngredientUnit {
  unit_id: number;
  unit_type: string;
}

// Fetch the food-category dropdown data. Backs the "Category" select on the
// Add / Edit ingredient form.
export const getIngredientCategories = async (): Promise<IngredientCategory[]> => {
  const data = await handleJson<{ success?: string; categories?: IngredientCategory[] }>(
    apiFetch(API_URLS.GET_INGREDIENT_CATEGORIES, { method: "GET" }),
  );
  return Array.isArray(data?.categories) ? data.categories : [];
};

// Fetch the units dropdown data. Backs the "Units" select on Step 1 and the
// custom-unit picker on Step 2 (unit conversion).
export const getUnitsList = async (): Promise<IngredientUnit[]> => {
  const data = await handleJson<{ success?: string; units?: IngredientUnit[] }>(
    apiFetch(API_URLS.GET_UNITS_LIST, { method: "GET" }),
  );
  return Array.isArray(data?.units) ? data.units : [];
};

// Single unit-conversion entry sent with CREATE_INGREDIENT — maps a unit
// (by its unit_id from the units master list) to its equivalent in grams.
export interface CreateIngredientUnit {
  unit_id: number;
  measure_gram: number;
}

// Payload for CREATE_INGREDIENT. Field names match the multipart form the
// backend expects one-for-one; conversion rows and tags are JSON-stringified
// when appended to the FormData. Backend accepts either `category` (name)
// or `category_id`, so both are optional at the type level.
//
// `micro` carries the micro-nutrient fields keyed by their backend
// snake_case names (vitamin_a, thiamine_b1, ...). All 29 keys are appended
// to the form (empty string when the user didn't enter a value) so the
// backend receives a consistent shape.
export interface CreateIngredientPayload {
  item_name: string;
  units: string;
  base_quantity: number;
  category?: string;
  category_id?: number;
  type: "VEG" | "NON_VEG";
  cals: number;
  AAFAdj_protein: number;
  gross_protein: number;
  total_fat: number;
  carbs?: number;
  net_carbs: number;
  GL: number;
  fibre?: number | "";
  tags: string[];
  unit: CreateIngredientUnit[];
  micro?: Record<string, string>;
  picture?: File | null;
}

// UPDATE_INGREDIENT accepts the same fields as CREATE_INGREDIENT, so the
// payload type is shared between both service calls.
export type UpdateIngredientPayload = CreateIngredientPayload;

// Serialise the ingredient payload into the multipart form the backend
// expects. Shared between create + update since the field list is
// identical. Callers append the request-specific bits (URL, method).
const buildIngredientFormData = (
  payload: CreateIngredientPayload,
): FormData => {
  const form = new FormData();
  form.append("item_name", payload.item_name);
  form.append("units", payload.units);
  form.append("base_quantity", String(payload.base_quantity));
  if (payload.category_id !== undefined) {
    form.append("category_id", String(payload.category_id));
  } else if (payload.category) {
    form.append("category", payload.category);
  }
  form.append("type", payload.type);
  form.append("cals", String(payload.cals));
  form.append("AAFAdj_protein", String(payload.AAFAdj_protein));
  form.append("gross_protein", String(payload.gross_protein));
  form.append("total_fat", String(payload.total_fat));
  if (payload.carbs !== undefined) {
    form.append("carbs", String(payload.carbs));
  }
  form.append("net_carbs", String(payload.net_carbs));
  form.append("GL", String(payload.GL));
  if (payload.fibre !== undefined && payload.fibre !== "") {
    form.append("fibre", String(payload.fibre));
  } else if (payload.fibre === "") {
    // User left the field blank — still send an empty value so the backend
    // sees the field consistently across all payloads.
    form.append("fibre", "");
  }
  form.append("tags", JSON.stringify(payload.tags));
  form.append("unit", JSON.stringify(payload.unit));
  // Micro-nutrient fields. Callers pass a map keyed by the backend snake_case
  // names; empty strings are appended so unfilled micros still round-trip.
  if (payload.micro) {
    Object.entries(payload.micro).forEach(([key, value]) => {
      form.append(key, value ?? "");
    });
  }
  if (payload.picture) {
    form.append("picture", payload.picture);
  }
  return form;
};

// Create a new ingredient (food item). Uses multipart/form-data so the
// optional picture can ride along with the text fields — apiFetch already
// skips setting Content-Type when the body is FormData, so the browser
// picks the correct boundary automatically.
export const createIngredient = (
  payload: CreateIngredientPayload,
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.CREATE_INGREDIENT, {
      method: "POST",
      body: buildIngredientFormData(payload),
    }),
  );

// Update an existing ingredient. Same multipart shape as create; the
// target record is identified by the food_id in the URL.
export const updateIngredient = (
  food_id: string | number,
  payload: UpdateIngredientPayload,
): Promise<any> =>
  handleJson(
    apiFetch(API_URLS.UPDATE_INGREDIENT(food_id), {
      method: "POST",
      body: buildIngredientFormData(payload),
    }),
  );

// Pull a filename out of a Content-Disposition header. Handles both the
// plain `filename="foo.xlsx"` form and the RFC 5987 `filename*=UTF-8''foo.xlsx`
// form. Returns null when no filename is present so callers can fall back.
const filenameFromContentDisposition = (header: string): string | null => {
  const starMatch = header.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i);
  if (starMatch) {
    try {
      return decodeURIComponent(starMatch[1].trim().replace(/^"|"$/g, ""));
    } catch {
      // Fall through to the plain form.
    }
  }
  const plainMatch = header.match(/filename\s*=\s*"?([^";]+)"?/i);
  return plainMatch ? plainMatch[1].trim() : null;
};

// One per-row failure from a bulk upload. Only populated when the sheet
// parses cleanly but some rows can't be applied (e.g. Modify referencing
// an ingredient that doesn't exist).
export interface UploadIngredientBulkErrorRow {
  sheet: string;
  row_number: number;
  item_name: string;
  error: string;
}

// One column-name mismatch when the sheet's header row doesn't match
// what the backend expects. Only populated on success === "0" responses.
export interface UploadIngredientBulkHeaderMismatch {
  column: string;
  expected: string;
  found: string;
  issue: string;
}

// UPLOAD_INGREDIENT_BULK_UPLOAD returns one of three shapes:
//   1. success "1" clean — added/updated counts, empty error_rows.
//   2. success "1" partial — some rows landed, others failed (error_rows populated).
//   3. success "0" header mismatch — no rows applied; expected/found headers +
//      header_mismatches describe the diff for the user to fix the sheet.
// All fields are optional so a single interface covers every shape.
export interface UploadIngredientBulkResponse {
  success: string;
  message: string;
  added_count?: number;
  updated_count?: number;
  added_items?: string[];
  updated_items?: string[];
  skipped_count?: number;
  skipped_rows?: unknown[];
  error_count?: number;
  error_rows?: UploadIngredientBulkErrorRow[];
  expected_headers?: string[];
  found_headers?: string[];
  header_mismatches?: UploadIngredientBulkHeaderMismatch[];
}

// POST the picked Excel file as multipart/form-data. The backend
// distinguishes "add new" vs. "modify existing" purely by matching
// row keys against existing records — the same endpoint serves both
// the Import new and Modify existing flows in the UI. The endpoint has
// been observed to key its counts with either the unprefixed shape
// (`added_count`) or an entity-prefixed shape (`ingredient_added_count`
// / `food_added_count`) — remap all variants here so
// BulkUploadResultView sees a consistent shape.
export const uploadIngredientBulkUpload = async (
  nutritionist_id: string | number,
  file: File,
): Promise<UploadIngredientBulkResponse> => {
  const form = new FormData();
  form.append("nutritionist_id", String(nutritionist_id));
  form.append("file", file);
  const raw = await handleJson<any>(
    apiFetch(API_URLS.UPLOAD_INGREDIENT_BULK_UPLOAD, {
      method: "POST",
      body: form,
    }),
  );
  return {
    ...raw,
    success: String(raw?.success ?? ""),
    message: raw?.message ?? "",
    added_count:
      raw?.added_count ?? raw?.ingredient_added_count ?? raw?.food_added_count,
    updated_count:
      raw?.updated_count ??
      raw?.ingredient_updated_count ??
      raw?.food_updated_count,
    skipped_count:
      raw?.skipped_count ??
      raw?.ingredient_skipped_count ??
      raw?.food_skipped_count,
    added_items:
      raw?.added_items ?? raw?.ingredient_added ?? raw?.food_added,
    updated_items:
      raw?.updated_items ?? raw?.ingredient_updated ?? raw?.food_updated,
    skipped_rows:
      raw?.skipped_rows ?? raw?.ingredient_skipped ?? raw?.food_skipped,
  };
};

// Re-post the same sheet with ?download_errors=true and stream the
// annotated Excel response back to the browser. Same multipart shape
// as the original upload — the query flag alone tells the backend to
// return the file (errors highlighted / column mismatches flagged)
// instead of the JSON summary, for the user to fix and re-upload.
export const downloadIngredientBulkUploadErrors = async (
  nutritionist_id: string | number,
  file: File,
): Promise<void> => {
  const form = new FormData();
  form.append("nutritionist_id", String(nutritionist_id));
  form.append("file", file);
  const response = await apiFetch(
    API_URLS.UPLOAD_INGREDIENT_BULK_UPLOAD_DOWNLOAD_ERRORS,
    { method: "POST", body: form },
  );
  if (!response.ok) {
    throw new Error(`Failed to download error file (HTTP ${response.status})`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const filename =
    filenameFromContentDisposition(disposition) ??
    `${file.name.replace(/\.xlsx?$/i, "")}_errors.xlsx`;
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

// Download the ingredient bulk-upload Excel template and trigger a
// browser save. The endpoint streams the file directly, so we grab the
// response as a Blob, materialise it via an object URL, and click a
// synthesised <a download> to save it. Variant maps to the flow the
// user picked from the Bulk upload dropdown.
export const downloadIngredientBulkUploadTemplate = async (
  variant: "normal" | "rename",
): Promise<void> => {
  const response = await apiFetch(
    API_URLS.DOWNLOAD_INGREDIENT_BULK_UPLOAD_TEMPLATE(variant),
    { method: "GET" },
  );
  if (!response.ok) {
    throw new Error(`Failed to download template (HTTP ${response.status})`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const filename =
    filenameFromContentDisposition(disposition) ??
    `ingredient_bulk_upload_${variant}.xlsx`;
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};
